import type { ElasoRoleRef } from "@elaso/shared/types/authorization";
import { useMemo } from "react";
import { useStore } from "zustand";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore, type Role } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";
import { useSelfPermissionsOnChannel } from "./use-accessible-channels";

const MODERATOR_ROLES = ["program-director", "program-manager", "tech-team"];

function hasModeratorRole(
	userRoles: ElasoRoleRef[],
	roster: Map<string, Role>,
	workspaceId: string | null | undefined
): boolean {
	const moderatorIds = new Set<string>();
	for (const role of roster.values()) {
		if (
			(!workspaceId || role.workspaceId === workspaceId) &&
			MODERATOR_ROLES.includes(role.name)
		) {
			moderatorIds.add(role.id);
		}
	}
	if (moderatorIds.size === 0) return false;
	return userRoles.some(
		(r) =>
			r.kind === "named" &&
			(!workspaceId || r.workspaceId === workspaceId) &&
			moderatorIds.has(r.id)
	);
}

/**
 * Whether the current user may moderate this channel's stage. Mirrors the
 * server's `canUserModerate`, the channel asset `moderate` permission, so the
 * moderation UI this gates matches the publish rights the LiveKit token grants.
 * The workspace-owner cascade is already in the channel permission, so no
 * separate role-name check is needed here.
 */
export function useStageModerator(channelId: string): boolean {
	return useSelfPermissionsOnChannel(channelId) === "moderate";
}

export function useIsOnStage(channelId: string): boolean {
	const user = useStore(authStore, (state) => state.user);
	const stageStates = useStore(coreStore, (state) => state.stageStates);

	return useMemo(() => {
		if (!user?.uid) return false;
		const stageState = stageStates.get(channelId);
		return stageState?.speakers.includes(user.uid) ?? false;
	}, [user?.uid, stageStates, channelId]);
}

export function useHasStageRequest(channelId: string): boolean {
	const user = useStore(authStore, (state) => state.user);
	const stageStates = useStore(coreStore, (state) => state.stageStates);

	return useMemo(() => {
		if (!user?.uid) return false;
		const stageState = stageStates.get(channelId);
		return stageState?.requests.some((r) => r.odId === user.uid) ?? false;
	}, [user?.uid, stageStates, channelId]);
}

/**
 * Standalone version of `useStageModerator` for callers that already hold the
 * user's refs, such as attendance-view computing rows per user without mounting
 * a hook per row. The roster and workspaceId must be passed in.
 */
export function isUserModerator(
	roles: ElasoRoleRef[],
	roster: Map<string, Role>,
	workspaceId?: string | null
): boolean {
	return hasModeratorRole(roles, roster, workspaceId ?? null);
}

// Kept for non-React callers that still want the workspaceId-resolving helper,
// which the uiStore-driven flows use.
export function useCurrentWorkspaceId(): string | null {
	return useStore(uiStore, (state) => state.currentWorkspaceId);
}
